import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  BarChart3, Loader2, TrendingUp, Search, MapPin, Landmark, Zap, Target,
  Users, Globe2, CalendarClock, Play, Pause, Trash2, Plus, ChevronDown, ChevronRight,
  Pencil,
  CheckCircle2, XCircle, MinusCircle,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from '../ui/tabs';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * ViewsBoostTab — mantenedor privado de cristian@plancity.cl.
 * Permite inflar coherentemente el contador de vistas de eventos por scope:
 * evento único, comuna, región, proveedor, o TODOS los eventos activos.
 * Además permite programar boosts automáticos en un rango de fechas.
 */
export default function ViewsBoostTab() {
  const [regions, setRegions] = useState([]);
  const [communes, setCommunes] = useState([]);
  const [providers, setProviders] = useState([]);
  const [mode, setMode] = useState('event'); // event|commune|region|provider|all
  const [eventQuery, setEventQuery] = useState('');
  const [eventOptions, setEventOptions] = useState([]);
  const [selectedEventIds, setSelectedEventIds] = useState([]);
  const [communeId, setCommuneId] = useState('');
  const [regionId, setRegionId] = useState('');
  const [providerId, setProviderId] = useState('');
  const [totalViews, setTotalViews] = useState(100);
  const [spreadDays, setSpreadDays] = useState(3);
  const [preview, setPreview] = useState(null);
  const [applying, setApplying] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/api/territories/regions`).then((r) => setRegions(r.data || [])).catch(() => setRegions([]));
    axios.get(`${API_URL}/api/territories/communes`).then((r) => setCommunes(r.data || [])).catch(() => setCommunes([]));
    // Admin can list providers for the boost scope.
    axios.get(`${API_URL}/api/admin/providers`).then((r) => setProviders(r.data || [])).catch(() => setProviders([]));
  }, []);

  useEffect(() => {
    if (mode !== 'event') return;
    const t = setTimeout(async () => {
      const q = eventQuery.trim();
      if (q.length < 2) { setEventOptions([]); return; }
      try {
        const res = await axios.get(`${API_URL}/api/events?search=${encodeURIComponent(q)}&limit=15`);
        setEventOptions(res.data.events || res.data || []);
      } catch { setEventOptions([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [eventQuery, mode]);

  const filteredCommunes = useMemo(() => {
    if (!regionId || mode !== 'commune') return communes;
    return communes.filter((c) => c.region_id === regionId);
  }, [communes, regionId, mode]);

  const buildPayload = () => {
    const base = { total_views: parseInt(totalViews, 10) || 0, spread_days: parseInt(spreadDays, 10) || 1 };
    if (mode === 'event' && selectedEventIds.length) base.event_ids = selectedEventIds;
    else if (mode === 'commune' && communeId) base.commune_id = communeId;
    else if (mode === 'region' && regionId) base.region_id = regionId;
    else if (mode === 'provider' && providerId) base.provider_id = providerId;
    else if (mode === 'all') base.all_active = true;
    return base;
  };

  const canSubmit = (() => {
    if (!(parseInt(totalViews, 10) > 0)) return false;
    if (mode === 'event') return selectedEventIds.length > 0;
    if (mode === 'commune') return !!communeId;
    if (mode === 'region') return !!regionId;
    if (mode === 'provider') return !!providerId;
    if (mode === 'all') return true;
    return false;
  })();

  const doPreview = async () => {
    if (!canSubmit) return;
    setPreviewing(true); setPreview(null);
    try {
      const res = await axios.post(`${API_URL}/api/admin/views/preview`, buildPayload());
      setPreview(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error en preview');
    } finally { setPreviewing(false); }
  };

  const doApply = async () => {
    if (!canSubmit) return;
    if (!window.confirm(`Vas a agregar ${totalViews} vistas distribuidas en ${preview?.matched_events || '?'} evento(s), en los últimos ${spreadDays} día(s). ¿Confirmas?`)) return;
    setApplying(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/views`, buildPayload());
      toast.success(`✓ ${res.data.views_added} vistas · ${res.data.visits_inserted} visitas · ${res.data.matched_events} evento(s)`);
      setLastResult(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al aplicar el boost');
    } finally { setApplying(false); }
  };

  const toggleEvent = (id) => {
    setSelectedEventIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
    setPreview(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="rounded-2xl bg-gradient-to-br from-indigo-50 to-white border border-indigo-100 p-5">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-indigo-600 text-white flex items-center justify-center shrink-0">
            <TrendingUp className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-[#064E3B]">Boost de Vistas</h2>
            <p className="text-sm text-gray-600 max-w-xl">
              Agrega paquetes de vistas por evento, comuna, región, proveedor o TODOS los eventos activos. Cada vista queda registrada como una <em>Visit</em> real con session_id único.
            </p>
          </div>
        </div>
      </div>

      {/* Boost inmediato */}
      <div className="rounded-2xl border bg-white p-5 space-y-4">
        <div className="text-sm font-semibold text-gray-700 uppercase tracking-wide">1 · Boost inmediato</div>
        <Tabs value={mode} onValueChange={(v) => { setMode(v); setPreview(null); }}>
          <TabsList className="bg-gray-100 flex-wrap h-auto">
            <TabsTrigger value="event" data-testid="mode-event"><Target className="w-3.5 h-3.5 mr-1" /> Por evento</TabsTrigger>
            <TabsTrigger value="commune" data-testid="mode-commune"><Landmark className="w-3.5 h-3.5 mr-1" /> Por comuna</TabsTrigger>
            <TabsTrigger value="region" data-testid="mode-region"><MapPin className="w-3.5 h-3.5 mr-1" /> Por región</TabsTrigger>
            <TabsTrigger value="provider" data-testid="mode-provider"><Users className="w-3.5 h-3.5 mr-1" /> Por proveedor</TabsTrigger>
            <TabsTrigger value="all" data-testid="mode-all"><Globe2 className="w-3.5 h-3.5 mr-1" /> Todos</TabsTrigger>
          </TabsList>

          <TabsContent value="event" className="mt-4 space-y-3">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input placeholder="Buscar evento por nombre…" value={eventQuery} onChange={(e) => setEventQuery(e.target.value)} className="pl-9" data-testid="boost-event-search" />
            </div>
            {eventOptions.length > 0 && (
              <div className="border rounded-lg divide-y max-h-64 overflow-y-auto">
                {eventOptions.map((ev) => (
                  <label key={ev.id} className="flex items-center gap-3 px-3 py-2 hover:bg-indigo-50 cursor-pointer" data-testid={`boost-event-option-${ev.id}`}>
                    <input type="checkbox" checked={selectedEventIds.includes(ev.id)} onChange={() => toggleEvent(ev.id)} className="w-4 h-4 rounded border-gray-300 text-indigo-600" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-[#064E3B] truncate">{ev.name}</div>
                      <div className="text-[11px] text-gray-500 truncate">{ev.commune_name} · {ev.views ?? 0} vistas actuales</div>
                    </div>
                  </label>
                ))}
              </div>
            )}
            {selectedEventIds.length > 0 && (<div className="text-xs text-indigo-700">{selectedEventIds.length} evento(s) seleccionado(s)</div>)}
          </TabsContent>

          <TabsContent value="commune" className="mt-4 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Región (filtro)</Label>
                <Select value={regionId || undefined} onValueChange={(v) => { setRegionId(v); setCommuneId(''); setPreview(null); }}>
                  <SelectTrigger data-testid="boost-region-filter"><SelectValue placeholder="Todas las regiones" /></SelectTrigger>
                  <SelectContent>{regions.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Comuna *</Label>
                <Select value={communeId || undefined} onValueChange={(v) => { setCommuneId(v); setPreview(null); }}>
                  <SelectTrigger data-testid="boost-commune-select"><SelectValue placeholder="Elige la comuna" /></SelectTrigger>
                  <SelectContent className="max-h-64">{filteredCommunes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="region" className="mt-4 space-y-3">
            <Label>Región *</Label>
            <Select value={regionId || undefined} onValueChange={(v) => { setRegionId(v); setPreview(null); }}>
              <SelectTrigger data-testid="boost-region-select"><SelectValue placeholder="Elige la región" /></SelectTrigger>
              <SelectContent>{regions.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
            </Select>
          </TabsContent>

          <TabsContent value="provider" className="mt-4 space-y-3">
            <Label>Proveedor *</Label>
            <Select value={providerId || undefined} onValueChange={(v) => { setProviderId(v); setPreview(null); }}>
              <SelectTrigger data-testid="boost-provider-select"><SelectValue placeholder="Elige el proveedor" /></SelectTrigger>
              <SelectContent className="max-h-64">
                {providers.map((p) => <SelectItem key={p.id} value={p.id}>{p.business_name || p.name || p.email}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-gray-500">Aplica el boost a todos los eventos activos de este proveedor.</p>
          </TabsContent>

          <TabsContent value="all" className="mt-4 space-y-2">
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm">
              <div className="font-semibold text-amber-900">⚠️ Alcance global</div>
              <div className="text-xs text-amber-800 mt-1">El boost se aplicará a <b>todos los eventos activos</b> de la plataforma. Usa esto solo para llenar métricas globales.</div>
            </div>
          </TabsContent>
        </Tabs>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2 border-t">
          <div>
            <Label>Total de vistas a agregar *</Label>
            <Input type="number" min="1" max="100000" value={totalViews} onChange={(e) => { setTotalViews(e.target.value); setPreview(null); }} data-testid="boost-total-views" />
          </div>
          <div>
            <Label>Distribuir en los últimos … días</Label>
            <Input type="number" min="1" max="90" value={spreadDays} onChange={(e) => setSpreadDays(e.target.value)} data-testid="boost-spread-days" />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 pt-2">
          <Button variant="outline" onClick={doPreview} disabled={!canSubmit || previewing} data-testid="boost-preview-btn">
            {previewing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <BarChart3 className="w-4 h-4 mr-1" />} Vista previa
          </Button>
          <Button onClick={doApply} disabled={!canSubmit || applying || !preview} className="bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-50" data-testid="boost-apply-btn">
            {applying ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Zap className="w-4 h-4 mr-1" />} Aplicar boost
          </Button>
          {!preview && canSubmit && (<span className="text-xs text-gray-500">Corre &quot;Vista previa&quot; antes para confirmar el alcance.</span>)}
        </div>

        {preview && (
          <div className="mt-2 rounded-lg border border-indigo-200 bg-indigo-50 p-3 text-sm">
            <div className="font-semibold text-indigo-900">{preview.matched_events} evento(s) afectado(s)</div>
            {preview.event_names.length > 0 && (<div className="text-xs text-indigo-700 mt-1">{preview.event_names.slice(0, 8).join(' · ')}{preview.matched_events > 8 && ` … y ${preview.matched_events - 8} más`}</div>)}
          </div>
        )}

        {lastResult && (
          <div className="mt-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm">
            <div className="font-semibold text-emerald-900">✓ Último boost aplicado</div>
            <div className="text-xs text-emerald-800 mt-1 space-y-0.5">
              <div>• {lastResult.views_added} vistas agregadas</div>
              <div>• {lastResult.visits_inserted} visitas sintéticas insertadas</div>
              <div>• {lastResult.unique_sessions_added} sessions únicas creadas</div>
              <div>• {lastResult.matched_events} evento(s) impactado(s)</div>
            </div>
          </div>
        )}
      </div>

      {/* Sección 2: Programación automática */}
      <SchedulerSection regions={regions} communes={communes} providers={providers} />
    </div>
  );
}


// ---------------------------------------------------------------------------
// Scheduler section — programa boosts automáticos por rango de fechas.
// ---------------------------------------------------------------------------
function SchedulerSection({ regions, communes, providers }) {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  // When set, `SchedulerForm` renders in edit mode instead of create mode.
  // The row's "Editar" button toggles this — the form is the same component
  // so validation and layout stay in one place.
  const [editingSchedule, setEditingSchedule] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/views/schedules`);
      setSchedules(res.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al cargar programaciones');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchList(); }, [fetchList]);

  const pause = async (id) => {
    try { await axios.post(`${API_URL}/api/admin/views/schedules/${id}/pause`); toast.success('Pausado'); fetchList(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };
  const resume = async (id) => {
    try { await axios.post(`${API_URL}/api/admin/views/schedules/${id}/resume`); toast.success('Reanudado'); fetchList(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };
  const remove = async (id) => {
    if (!window.confirm('Eliminar esta programación? No se pueden revertir las vistas ya inyectadas.')) return;
    try { await axios.delete(`${API_URL}/api/admin/views/schedules/${id}`); toast.success('Eliminado'); fetchList(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const fmtDate = (iso) => {
    // Formato dd-mm-aaaa hh:mm en zona Chile
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      const dt = new Intl.DateTimeFormat('es-CL', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
        hour12: false,
        timeZone: 'America/Santiago',
      }).formatToParts(d).reduce((acc, p) => { acc[p.type] = p.value; return acc; }, {});
      return `${dt.day}-${dt.month}-${dt.year} ${dt.hour}:${dt.minute}`;
    } catch { return iso; }
  };
  const fmtDay = (iso) => {
    // Formato dd-mm-aaaa (solo fecha)
    if (!iso) return '—';
    try {
      const [y, m, d] = iso.split('-');
      return `${d}-${m}-${y}`;
    } catch { return iso; }
  };

  const scopeLabel = (s) => {
    if (s.scope === 'event') return 'Evento único';
    if (s.scope === 'commune') return `Comuna: ${communes.find((c) => c.id === s.commune_id)?.name || s.commune_id}`;
    if (s.scope === 'region') return `Región: ${regions.find((r) => r.id === s.region_id)?.name || s.region_id}`;
    if (s.scope === 'provider') return `Proveedor: ${providers.find((p) => p.id === s.provider_id)?.business_name || s.provider_id}`;
    if (s.scope === 'all') return 'Todos los activos';
    return s.scope;
  };

  return (
    <div className="rounded-2xl border bg-white p-5 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold text-gray-700 uppercase tracking-wide flex items-center gap-2">
            <CalendarClock className="w-4 h-4" /> 2 · Programación automática
          </div>
          <p className="text-xs text-gray-500 mt-1">Ejecuta boosts diarios entre horas peak — cada corrida usa un rango aleatorio de vistas para que aumenten orgánicamente.</p>
        </div>
        <Button onClick={() => { setEditingSchedule(null); setShowForm(!showForm || editingSchedule); }} data-testid="scheduler-toggle-form">
          <Plus className="w-4 h-4 mr-1" /> Nueva programación
        </Button>
      </div>

      {(showForm || editingSchedule) && (
        <SchedulerForm
          regions={regions}
          communes={communes}
          providers={providers}
          schedule={editingSchedule}
          onSaved={() => { setShowForm(false); setEditingSchedule(null); fetchList(); }}
          onCancel={() => { setShowForm(false); setEditingSchedule(null); }}
        />
      )}

      {loading && schedules.length === 0 ? (
        <div className="text-center text-gray-500 py-8"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
      ) : schedules.length === 0 ? (
        <div className="text-center text-gray-500 py-8 border border-dashed rounded-lg">
          Sin programaciones. Crea una para automatizar boosts diarios.
        </div>
      ) : (
        <div className="divide-y border rounded-lg">
          {schedules.map((s) => (
            <ScheduleRow
              key={s.id}
              schedule={s}
              scopeLabel={scopeLabel(s)}
              fmtDate={fmtDate}
              fmtDay={fmtDay}
              onPause={() => pause(s.id)}
              onResume={() => resume(s.id)}
              onDelete={() => remove(s.id)}
              onEdit={() => { setEditingSchedule(s); setShowForm(false); }}
            />
          ))}
        </div>
      )}
    </div>
  );
}


function SchedulerForm({ regions, communes, providers, onSaved, onCancel, schedule = null }) {
  const isEdit = !!schedule;
  const todayIso = new Date().toISOString().slice(0, 10);
  const [name, setName] = useState(schedule?.name || '');
  const [scope, setScope] = useState(schedule?.scope || 'all');
  const [communeId, setCommuneId] = useState(schedule?.commune_id || '');
  const [regionId, setRegionId] = useState(schedule?.region_id || '');
  const [providerId, setProviderId] = useState(schedule?.provider_id || '');
  const [viewsMin, setViewsMin] = useState(schedule?.views_min ?? 20);
  const [viewsMax, setViewsMax] = useState(schedule?.views_max ?? 60);
  const [runsPerDay, setRunsPerDay] = useState(schedule?.runs_per_day ?? 3);
  const [hourFrom, setHourFrom] = useState(schedule?.hour_from ?? 10);
  const [hourTo, setHourTo] = useState(schedule?.hour_to ?? 22);
  const [startDate, setStartDate] = useState(schedule?.start_date ? String(schedule.start_date).slice(0, 10) : todayIso);
  const [endDate, setEndDate] = useState(schedule?.end_date ? String(schedule.end_date).slice(0, 10) : todayIso);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!name.trim()) { toast.error('Nombre requerido'); return; }
    if (parseInt(viewsMax, 10) < parseInt(viewsMin, 10)) { toast.error('viewsMax debe ser ≥ viewsMin'); return; }
    setSaving(true);
    const payload = {
      name: name.trim(), scope,
      views_min: parseInt(viewsMin, 10),
      views_max: parseInt(viewsMax, 10),
      runs_per_day: parseInt(runsPerDay, 10),
      hour_from: parseInt(hourFrom, 10),
      hour_to: parseInt(hourTo, 10),
      start_date: startDate, end_date: endDate,
    };
    if (scope === 'commune') payload.commune_id = communeId;
    if (scope === 'region') payload.region_id = regionId;
    if (scope === 'provider') payload.provider_id = providerId;
    try {
      if (isEdit) {
        await axios.put(`${API_URL}/api/admin/views/schedules/${schedule.id}`, payload);
        toast.success('Programación actualizada');
      } else {
        await axios.post(`${API_URL}/api/admin/views/schedules`, payload);
        toast.success('Programación creada');
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || (isEdit ? 'Error al actualizar' : 'Error al crear'));
    } finally { setSaving(false); }
  };

  return (
    <div className="rounded-lg border-2 border-indigo-200 bg-indigo-50/40 p-4 space-y-3" data-testid="scheduler-form">
      {isEdit && (
        <div className="text-xs font-semibold text-indigo-700 uppercase tracking-wide" data-testid="scheduler-edit-badge">
          Editando: {schedule.name}
        </div>
      )}
      <div>
        <Label>Nombre</Label>
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ej: Boost diario noviembre" data-testid="scheduler-name" />
      </div>
      <div>
        <Label>Alcance</Label>
        <Select value={scope} onValueChange={setScope}>
          <SelectTrigger data-testid="scheduler-scope"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los activos</SelectItem>
            <SelectItem value="commune">Por comuna</SelectItem>
            <SelectItem value="region">Por región</SelectItem>
            <SelectItem value="provider">Por proveedor</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {scope === 'commune' && (
        <Select value={communeId || undefined} onValueChange={setCommuneId}>
          <SelectTrigger data-testid="scheduler-commune"><SelectValue placeholder="Elige la comuna" /></SelectTrigger>
          <SelectContent className="max-h-64">{communes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
        </Select>
      )}
      {scope === 'region' && (
        <Select value={regionId || undefined} onValueChange={setRegionId}>
          <SelectTrigger data-testid="scheduler-region"><SelectValue placeholder="Elige la región" /></SelectTrigger>
          <SelectContent>{regions.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
        </Select>
      )}
      {scope === 'provider' && (
        <Select value={providerId || undefined} onValueChange={setProviderId}>
          <SelectTrigger data-testid="scheduler-provider"><SelectValue placeholder="Elige el proveedor" /></SelectTrigger>
          <SelectContent className="max-h-64">{providers.map((p) => <SelectItem key={p.id} value={p.id}>{p.business_name || p.name}</SelectItem>)}</SelectContent>
        </Select>
      )}

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Vistas mínimas / corrida</Label>
          <Input type="number" min="1" value={viewsMin} onChange={(e) => setViewsMin(e.target.value)} data-testid="scheduler-views-min" />
        </div>
        <div>
          <Label>Vistas máximas / corrida</Label>
          <Input type="number" min="1" value={viewsMax} onChange={(e) => setViewsMax(e.target.value)} data-testid="scheduler-views-max" />
        </div>
        <div>
          <Label>Corridas por día</Label>
          <Input type="number" min="1" max="24" value={runsPerDay} onChange={(e) => setRunsPerDay(e.target.value)} data-testid="scheduler-runs-per-day" />
        </div>
        <div className="grid grid-cols-2 gap-1">
          <div>
            <Label>Desde h.</Label>
            <Input type="number" min="0" max="23" value={hourFrom} onChange={(e) => setHourFrom(e.target.value)} data-testid="scheduler-hour-from" />
          </div>
          <div>
            <Label>Hasta h.</Label>
            <Input type="number" min="1" max="24" value={hourTo} onChange={(e) => setHourTo(e.target.value)} data-testid="scheduler-hour-to" />
          </div>
        </div>
        <div>
          <Label>Fecha inicio</Label>
          <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="scheduler-start-date" />
        </div>
        <div>
          <Label>Fecha fin</Label>
          <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="scheduler-end-date" />
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" onClick={onCancel}>Cancelar</Button>
        <Button onClick={submit} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="scheduler-save">
          {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null} {isEdit ? 'Guardar cambios' : 'Guardar'}
        </Button>
      </div>
    </div>
  );
}


// Fila individual de schedule con historial de corridas colapsable.
function ScheduleRow({ schedule: s, scopeLabel, fmtDate, fmtDay, onPause, onResume, onDelete, onEdit }) {
  const [expanded, setExpanded] = useState(false);
  const [runs, setRuns] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadRuns = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/views/schedules/${s.id}/runs?limit=30`);
      setRuns(res.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al cargar historial');
      setRuns([]);
    } finally { setLoading(false); }
  }, [s.id]);

  const toggle = () => {
    if (!expanded && runs === null) loadRuns();
    setExpanded(!expanded);
  };

  const statusPill = (status) => {
    if (status === 'executed') return (
      <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-semibold">
        <CheckCircle2 className="w-3 h-3" /> Ejecutado
      </span>
    );
    if (status === 'skipped') return (
      <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-semibold">
        <MinusCircle className="w-3 h-3" /> Omitido
      </span>
    );
    if (status === 'failed') return (
      <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-semibold">
        <XCircle className="w-3 h-3" /> Falló
      </span>
    );
    return <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-700">{status}</span>;
  };

  return (
    <div className="p-3" data-testid={`schedule-${s.id}`}>
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={toggle}
          className="p-1 rounded hover:bg-gray-100 text-gray-500"
          aria-label={expanded ? 'Ocultar historial' : 'Ver historial'}
          data-testid={`schedule-history-toggle-${s.id}`}
        >
          {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-sm">{s.name}</span>
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-700">{scopeLabel}</span>
            <span className={`text-[10px] px-1.5 py-0.5 rounded ${s.status === 'active' ? 'bg-emerald-100 text-emerald-700' : s.status === 'paused' ? 'bg-gray-200 text-gray-700' : 'bg-blue-100 text-blue-700'}`}>
              {s.status}
            </span>
          </div>
          <div className="text-xs text-gray-600 mt-1 flex items-center gap-3 flex-wrap">
            <span>📊 {s.views_min}–{s.views_max} vistas × {s.runs_per_day}/día</span>
            <span>🕐 {s.hour_from}:00–{s.hour_to}:00 CL</span>
            <span>📅 {fmtDay(s.start_date)} → {fmtDay(s.end_date)}</span>
            <span>Próxima: {fmtDate(s.next_run_at)}</span>
            <span>Ejecuciones: {s.runs_executed} ({s.total_views_added} vistas)</span>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {s.status === 'active' ? (
            <Button size="sm" variant="outline" onClick={onPause} data-testid={`schedule-pause-${s.id}`}><Pause className="w-3.5 h-3.5" /></Button>
          ) : s.status === 'paused' ? (
            <Button size="sm" variant="outline" onClick={onResume} data-testid={`schedule-resume-${s.id}`}><Play className="w-3.5 h-3.5" /></Button>
          ) : null}
          <Button size="sm" variant="outline" onClick={onEdit} data-testid={`schedule-edit-${s.id}`} title="Editar programación"><Pencil className="w-3.5 h-3.5" /></Button>
          <Button size="sm" variant="outline" onClick={onDelete} className="border-red-300 text-red-600 hover:bg-red-50" data-testid={`schedule-delete-${s.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
        </div>
      </div>

      {expanded && (
        <div className="mt-3 ml-6 border-l-2 border-gray-200 pl-4" data-testid={`schedule-history-${s.id}`}>
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Historial de corridas</div>
            <button type="button" onClick={loadRuns} className="text-xs text-indigo-600 hover:underline">Actualizar</button>
          </div>
          {loading ? (
            <div className="text-center py-4 text-gray-500"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
          ) : (runs || []).length === 0 ? (
            <div className="text-xs text-gray-500 py-3 italic">Sin corridas registradas todavía.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-gray-500">
                  <tr className="border-b">
                    <th className="text-left py-1.5 pr-3 font-medium">Fecha y hora</th>
                    <th className="text-left py-1.5 pr-3 font-medium">Estado</th>
                    <th className="text-right py-1.5 pr-3 font-medium">Vistas</th>
                    <th className="text-right py-1.5 pr-3 font-medium">Visitas</th>
                    <th className="text-right py-1.5 pr-3 font-medium">Eventos</th>
                    <th className="text-left py-1.5 font-medium">Detalle</th>
                  </tr>
                </thead>
                <tbody>
                  {runs.map((r) => (
                    <tr key={r.id} className="border-b last:border-0 hover:bg-gray-50" data-testid={`run-${r.id}`}>
                      <td className="py-1.5 pr-3 font-mono">{fmtDate(r.run_at)}</td>
                      <td className="py-1.5 pr-3">{statusPill(r.status)}</td>
                      <td className="py-1.5 pr-3 text-right">{r.views_added.toLocaleString('es-CL')}</td>
                      <td className="py-1.5 pr-3 text-right">{r.visits_inserted.toLocaleString('es-CL')}</td>
                      <td className="py-1.5 pr-3 text-right">{r.events_count}</td>
                      <td className="py-1.5 text-gray-500 truncate max-w-xs" title={r.error_message || ''}>{r.error_message || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

